import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../component/layout/header";
import PageHeader from "../component/layout/pageheader";
import FooterSection from "../component/layout/footer";
import {
  FiSend,
  FiSearch,
  FiImage,
  FiFile,
  FiSmile,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiClock,
  FiRefreshCw,
  FiMic,
  FiLink,
  FiLock,
} from "react-icons/fi";
import { IoMdNotificationsOutline } from "react-icons/io";
import { FaFire, FaHeart, FaBolt } from "react-icons/fa";
import axios from "axios";
import echo from "../utils/echo";

const MESSAGE_GROUP_WINDOW_MS = 60 * 1000;
const PAGE_SIZE = 20;
const LAST_SEEN_WINDOW_MS = 2 * 60 * 1000;

const EMOTICON_MAP = {
  ":)": "🙂",
  ":-)": "🙂",
  ":D": "😄",
  ";)": "😉",
  ":(": "🙁",
  "<3": "❤️",
  ":P": "😛",
  ":p": "😛",
  ":o": "😮",
  ":O": "😮",
};

const HOT_EMOJIS = ["🔥", "😘", "❤️", "😉", "💋"];
const REACTION_SET = ["🔥", "❤️", "😘"];

const getItemImage = (item = {}) =>
  item?.imgUrl ||
  item?.image ||
  item?.avatar ||
  item?.photo ||
  item?.thumbnail ||
  item?.profile_photo ||
  null;

const normalizeUserItem = (raw = {}) => ({
  ...raw,
  id: raw?.id || raw?.user_id || raw?.member_id,
  name: raw?.name || raw?.full_name || raw?.username || "Member",
  imgUrl: getItemImage(raw),
  is_online: Boolean(raw?.is_online || raw?.online),
  last_seen: raw?.last_seen || raw?.lastSeen || null,
});

const normalizeGroupItem = (raw = {}) => ({
  ...raw,
  id: raw?.id || raw?.group_id || raw?.community_id,
  name: raw?.name || raw?.group_name || raw?.title || "Group",
  imgUrl: getItemImage(raw),
  type: "group",
});

const normalizeConversationItem = (raw = {}) => {
  const type = raw?.type || raw?.chat_type || (raw?.group_id ? "group" : "direct");
  const receiverId =
    raw?.receiver_id ||
    raw?.target_id ||
    raw?.recipient_id ||
    (type === "group" ? raw?.group_id : raw?.user_id);

  return {
    ...raw,
    id: raw?.id || raw?.conversation_id,
    name: raw?.name || raw?.receiver_name || raw?.group_name || raw?.title || "Chat",
    imgUrl: getItemImage(raw),
    type,
    receiver_id: receiverId,
    lastMsg: raw?.lastMsg || raw?.last_message || raw?.message || "",
    unread: Number(raw?.unread || raw?.unread_count || 0),
    updatedAt: raw?.updatedAt || raw?.updated_at || raw?.last_message_at || null,
    is_online: Boolean(raw?.is_online || raw?.online),
    last_seen: raw?.last_seen || raw?.lastSeen || null,
  };
};

const replaceEmoticons = (text = "") => {
  let output = text;
  Object.entries(EMOTICON_MAP).forEach(([emoticon, emoji]) => {
    output = output.split(emoticon).join(emoji);
  });
  return output;
};

const getInitials = (name = "?") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const parseMessageTime = (value) => {
  const date = new Date(value || Date.now());
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const formatTime = (value) =>
  parseMessageTime(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const getStatusRank = (status) => {
  if (status === "read") return 3;
  if (status === "delivered") return 2;
  if (status === "sent") return 1;
  return 0;
};

const updateStatusSafely = (message, targetStatus) => {
  if (!message?.sent || message?.failed) return message;
  if (getStatusRank(targetStatus) <= getStatusRank(message.status)) return message;
  return { ...message, status: targetStatus };
};

const getMessageText = (message) =>
  message?.text || message?.message || message?.content || "";

const extractUrl = (text = "") => {
  const match = text.match(/https?:\/\/[^\s]+/i);
  return match ? match[0] : null;
};

const getPreviewForMessage = (messageText) => {
  const url = extractUrl(messageText);
  if (!url) return null;

  return {
    url,
    title: "Shared link",
    description: "Tap to open preview",
    kind: "link",
  };
};

const buildQuickReplies = (messages, activeChatType) => {
  const lastIncoming = [...messages].reverse().find((msg) => !msg.sent);
  const content = (lastIncoming?.text || "").toLowerCase();

  if (content.includes("photo") || content.includes("pic")) {
    return ["Send me one too 😏", "You look amazing", "Show me more"];
  }

  if (content.includes("where") || content.includes("location")) {
    return ["I am close by", "Want to meet tonight?", "Send me your pin"];
  }

  if (content.includes("group") || activeChatType === "group") {
    return ["I am in", "Tell me more", "Who else is joining?"];
  }

  return ["Tell me more", "Sent you a photo", "Are you free tonight?"];
};

const normalizeMessage = ({
  raw,
  currentUser,
  fallbackSent = false,
  temp = false,
  forcedStatus,
}) => {
  const senderId = raw?.sender_id || raw?.senderId || raw?.from_id || null;
  const sent =
    typeof raw?.sent === "boolean"
      ? raw.sent
      : senderId
      ? String(senderId) === String(currentUser?.id)
      : fallbackSent;

  const status =
    forcedStatus ||
    raw?.status ||
    (raw?.read_at ? "read" : raw?.delivered_at ? "delivered" : "sent");

  const createdAt = raw?.created_at || raw?.time || raw?.createdAt || new Date().toISOString();

  return {
    id: raw?.id || `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    text: replaceEmoticons(getMessageText(raw)),
    time: createdAt,
    sent,
    senderId: senderId || (sent ? currentUser?.id : null),
    senderName: raw?.sender_name || raw?.senderName || (sent ? currentUser?.name || "You" : "Member"),
    failed: Boolean(raw?.failed),
    status: sent ? status : "received",
    hasAttachment: Boolean(raw?.has_attachment || raw?.attachment_url),
    attachmentUrl: raw?.attachment_url || null,
    preview: getPreviewForMessage(getMessageText(raw)),
    isTemp: temp,
  };
};

const ChatPage = () => {
  const location = useLocation();
  const token = localStorage.getItem("token");

  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isPeerTyping, setIsPeerTyping] = useState(false);
  const [typingLabel, setTypingLabel] = useState("typing...");

  const [messagePage, setMessagePage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [openReactionFor, setOpenReactionFor] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const userCarouselRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const peerTypingTimeoutRef = useRef(null);
  const longPressTimerRef = useRef(null);

  const { state } = location;

  const getActiveChatType = () => activeChat?.type || null;

  const filteredConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((c) => c.name?.toLowerCase().includes(query));
  }, [conversations, searchQuery]);

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((group) => group?.name?.toLowerCase().includes(query));
  }, [groups, searchQuery]);

  const groupedMessages = useMemo(() => {
    const sorted = [...messages].sort(
      (a, b) => parseMessageTime(a.time).getTime() - parseMessageTime(b.time).getTime()
    );

    const clusters = [];

    sorted.forEach((msg) => {
      const lastCluster = clusters[clusters.length - 1];
      const msgTime = parseMessageTime(msg.time).getTime();

      if (!lastCluster) {
        clusters.push({
          id: `cluster-${msg.id}`,
          sent: msg.sent,
          senderId: msg.senderId,
          senderName: msg.senderName,
          messages: [msg],
        });
        return;
      }

      const lastMsg = lastCluster.messages[lastCluster.messages.length - 1];
      const lastTime = parseMessageTime(lastMsg.time).getTime();

      const isSameSender =
        String(lastCluster.senderId ?? "") === String(msg.senderId ?? "") &&
        lastCluster.sent === msg.sent;
      const isWithinWindow = msgTime - lastTime <= MESSAGE_GROUP_WINDOW_MS;

      if (isSameSender && isWithinWindow) {
        lastCluster.messages.push(msg);
      } else {
        clusters.push({
          id: `cluster-${msg.id}`,
          sent: msg.sent,
          senderId: msg.senderId,
          senderName: msg.senderName,
          messages: [msg],
        });
      }
    });

    return clusters;
  }, [messages]);

  const quickReplies = useMemo(
    () => buildQuickReplies(messages, getActiveChatType()),
    [messages, activeChat]
  );

  const lastReadMessage = useMemo(
    () => [...messages].reverse().find((msg) => msg.sent && msg.status === "read"),
    [messages]
  );

  const activePresenceLabel = useMemo(() => {
    if (!activeChat) return "";
    if (isPeerTyping) return typingLabel;

    const sourceItem =
      conversations.find((c) => c.id === activeChat?.conversation_id) ||
      users.find((u) => String(u.id) === String(activeChat?.receiver_id));

    if (sourceItem?.is_online) return "online";

    const latestTs =
      sourceItem?.last_seen || sourceItem?.updatedAt || sourceItem?.updated_at || messages[messages.length - 1]?.time;

    if (!latestTs) return "last seen recently";

    const lastSeenTime = parseMessageTime(latestTs).getTime();
    if (Date.now() - lastSeenTime <= LAST_SEEN_WINDOW_MS) return "online";

    return `last seen ${parseMessageTime(latestTs).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }, [activeChat, conversations, users, messages, isPeerTyping, typingLabel]);

  const mergeMessages = useCallback((incoming, { prepend = false } = {}) => {
    setMessages((prev) => {
      const map = new Map();
      [...prev, ...incoming].forEach((msg) => {
        map.set(String(msg.id), msg);
      });

      const merged = Array.from(map.values()).sort(
        (a, b) => parseMessageTime(a.time).getTime() - parseMessageTime(b.time).getTime()
      );

      if (!prepend) return merged;

      const prevIds = new Set(prev.map((m) => String(m.id)));
      const onlyOlder = merged.filter((msg) => !prevIds.has(String(msg.id)) || prev.some((p) => String(p.id) === String(msg.id)));
      return onlyOlder;
    });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [groupedMessages.length]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Error fetching current user", err);
      }
    };

    if (token) fetchCurrentUser();
  }, [token]);

  const getConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/conversation/all-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = res?.data;
      const rawItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setConversations(rawItems.map(normalizeConversationItem).filter((item) => item.id));
    } catch (err) {
      console.error("Error fetching chat list", err);
    } finally {
      setLoadingChats(false);
    }
  }, [token]);

  const getChatUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/chat-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = res?.data;
      const rawItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setUsers(rawItems.map(normalizeUserItem).filter((item) => item.id));
    } catch (err) {
      console.error("Error fetching chat users", err);
    }
  }, [token]);

  const getUserGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = res?.data;
      const rawItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.groups)
        ? payload.groups
        : [];
      setGroups(rawItems.map(normalizeGroupItem).filter((item) => item.id));
    } catch (err) {
      console.error("Error fetching groups", err);
    }
  }, [token]);

  const getMessages = useCallback(
    async (conversationId, page = 1, { prepend = false } = {}) => {
      if (!conversationId) {
        setMessages([]);
        setHasMoreMessages(false);
        return;
      }

      if (prepend) setLoadingMoreMessages(true);

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_MAIN_SITE_URL}/message`,
          { conversationId, page, per_page: PAGE_SIZE },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const payload = res.data;
        const rawItems = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        const normalized = rawItems.map((raw) =>
          normalizeMessage({
            raw,
            currentUser,
          })
        );

        if (prepend) {
          const container = messagesContainerRef.current;
          const previousHeight = container?.scrollHeight || 0;
          setMessages((prev) => {
            const map = new Map();
            [...normalized, ...prev].forEach((msg) => map.set(String(msg.id), msg));
            return Array.from(map.values()).sort(
              (a, b) => parseMessageTime(a.time).getTime() - parseMessageTime(b.time).getTime()
            );
          });

          requestAnimationFrame(() => {
            if (container) {
              const newHeight = container.scrollHeight;
              container.scrollTop = newHeight - previousHeight;
            }
          });
        } else {
          setMessages(normalized);
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
          });
        }

        const hasMore = rawItems.length >= PAGE_SIZE;
        setHasMoreMessages(hasMore);
        setMessagePage(page);
      } catch (err) {
        console.error("Error fetching messages", err);
      } finally {
        setLoadingMoreMessages(false);
      }
    },
    [token, currentUser]
  );

  useEffect(() => {
    if (!token || !currentUser) return;

    getChatUsers();
    getUserGroups();
    getConversations();
  }, [token, currentUser, getChatUsers, getUserGroups, getConversations]);

  useEffect(() => {
    if (!state?.id || !state?.type) return;

    if (state.type === "profile") {
      const selectedUser = users.find((u) => String(u.id) === String(state.id));
      if (selectedUser) {
        setActiveChat({
          ...selectedUser,
          type: "direct",
          receiver_id: selectedUser.id,
          conversation_id: null,
        });
        setMessages([]);
      }
      return;
    }

    if (state.type === "group") {
      const selectedGroup = groups.find((g) => String(g.id) === String(state.id));
      if (selectedGroup) {
        setActiveChat({
          ...selectedGroup,
          type: "group",
          receiver_id: selectedGroup.id,
          conversation_id: null,
        });
        setMessages([]);
      }
    }
  }, [state, users, groups]);

  useEffect(() => {
    const handleCarouselScroll = () => {
      if (!userCarouselRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = userCarouselRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    };

    const carousel = userCarouselRef.current;
    if (!carousel) return;

    carousel.addEventListener("scroll", handleCarouselScroll);
    handleCarouselScroll();

    return () => carousel.removeEventListener("scroll", handleCarouselScroll);
  }, [users, groups]);

  const scrollCarousel = (direction) => {
    if (!userCarouselRef.current) return;
    userCarouselRef.current.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  };

  const selectConversation = useCallback(
    (chat) => {
      setActiveChat({
        ...chat,
        receiver_id: chat.receiver_id,
        type: chat.type,
        conversation_id: chat.id,
      });
      setHasMoreMessages(true);
      getMessages(chat.id, 1);
    },
    [getMessages]
  );

  const handleSelectUser = useCallback(
    (user) => {
      const existingConversation = conversations.find(
        (c) => String(c.receiver_id) === String(user.id) && c.type === "direct"
      );

      if (existingConversation) {
        selectConversation(existingConversation);
      } else {
        setActiveChat({
          ...user,
          type: "direct",
          receiver_id: user.id,
          conversation_id: null,
        });
        setMessages([]);
      }
    },
    [conversations, selectConversation]
  );

  const handleSelectGroup = useCallback(
    (group) => {
      const existingConversation = conversations.find(
        (c) => String(c.receiver_id) === String(group.id) && c.type === "group"
      );

      if (existingConversation) {
        selectConversation(existingConversation);
      } else {
        setActiveChat({
          ...group,
          type: "group",
          receiver_id: group.id,
          conversation_id: null,
        });
        setMessages([]);
      }
    },
    [conversations, selectConversation]
  );

  const sendTypingWhisper = useCallback(() => {
    if (!currentUser?.id || !activeChat?.receiver_id) return;

    try {
      echo.private(`chat.${activeChat.receiver_id}`).whisper("typing", {
        from: currentUser.id,
        chatType: activeChat.type,
        name: currentUser.name,
      });
    } catch (error) {
      // Whisper support depends on backend channel config.
    }
  }, [activeChat, currentUser]);

  const handleMessageInput = (value) => {
    setMessage(replaceEmoticons(value));

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    sendTypingWhisper();

    typingTimeoutRef.current = setTimeout(() => {
      sendTypingWhisper();
    }, 1400);
  };

  const insertHotEmoji = () => {
    const randomEmoji = HOT_EMOJIS[Math.floor(Math.random() * HOT_EMOJIS.length)];
    setMessage((prev) => `${prev}${prev ? " " : ""}${randomEmoji}`);
  };

  const markMessageDeliveredThenRead = useCallback((messageId) => {
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          String(msg.id) === String(messageId) ? updateStatusSafely(msg, "delivered") : msg
        )
      );
    }, 350);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          String(msg.id) === String(messageId) ? updateStatusSafely(msg, "read") : msg
        )
      );
    }, 1200);
  }, []);

  const handleSendMessage = async (event, presetText) => {
    event?.preventDefault?.();

    const outgoingText = replaceEmoticons((presetText ?? message).trim());
    if (!outgoingText || !activeChat) return;

    const tempId = `tmp-${Date.now()}`;
    const tempMessage = normalizeMessage({
      raw: {
        id: tempId,
        message: outgoingText,
        created_at: new Date().toISOString(),
        sender_id: currentUser?.id,
      },
      currentUser,
      fallbackSent: true,
      temp: true,
      forcedStatus: "sending",
    });

    setMessages((prev) => [...prev, tempMessage]);
    setMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_MAIN_SITE_URL}/message/send`,
        {
          receiver_id: activeChat.receiver_id,
          message: outgoingText,
          type: getActiveChatType(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const serverMsg = response?.data?.message || {};
      const normalized = normalizeMessage({
        raw: serverMsg,
        currentUser,
        fallbackSent: true,
        forcedStatus: "sent",
      });

      setMessages((prev) =>
        prev.map((msg) => (String(msg.id) === String(tempId) ? normalized : msg))
      );

      if (!activeChat.conversation_id && serverMsg?.conversation_id) {
        setActiveChat((prev) =>
          prev
            ? {
                ...prev,
                conversation_id: serverMsg.conversation_id,
              }
            : prev
        );
      }

      markMessageDeliveredThenRead(normalized.id);
      getConversations();
    } catch (error) {
      console.error("Failed to send message", error);
      setMessages((prev) =>
        prev.map((msg) =>
          String(msg.id) === String(tempId)
            ? {
                ...msg,
                failed: true,
                status: "failed",
              }
            : msg
        )
      );
    }
  };

  const retryFailedMessage = async (failedMessage) => {
    setMessages((prev) => prev.filter((m) => String(m.id) !== String(failedMessage.id)));
    await handleSendMessage(null, failedMessage.text);
  };

  const handleMessagesScroll = async () => {
    const container = messagesContainerRef.current;
    if (!container || loadingMoreMessages || !hasMoreMessages || !activeChat?.conversation_id) return;

    if (container.scrollTop > 60) return;

    const nextPage = messagePage + 1;
    await getMessages(activeChat.conversation_id, nextPage, { prepend: true });
  };

  useEffect(() => {
    if (!currentUser?.id) return;

    const channel = echo.private(`chat.${currentUser.id}`);

    channel.listen("MessageSent", (eventData) => {
      if (String(eventData?.sender_id) === String(currentUser.id)) return;

      const incomingMessage = normalizeMessage({
        raw: {
          id: eventData?.id,
          message: eventData?.text,
          created_at: eventData?.time,
          sender_id: eventData?.sender_id,
          sender_name: eventData?.sender_name,
          has_attachment: eventData?.has_attachment,
        },
        currentUser,
      });

      const isCurrentConversation =
        eventData?.conversation_id &&
        String(eventData.conversation_id) === String(activeChat?.conversation_id);

      const isMatchingPendingThread =
        !activeChat?.conversation_id &&
        activeChat?.type === eventData?.type &&
        ((eventData?.type === "direct" &&
          String(activeChat?.receiver_id) === String(eventData?.sender_id)) ||
          (eventData?.type === "group" &&
            String(activeChat?.receiver_id) === String(eventData?.receiver_id)));

      if (isCurrentConversation || isMatchingPendingThread) {
        mergeMessages([incomingMessage]);

        if (isMatchingPendingThread && eventData?.conversation_id) {
          setActiveChat((prev) =>
            prev
              ? {
                  ...prev,
                  conversation_id: eventData.conversation_id,
                }
              : prev
          );
        }
      }

      setIsPeerTyping(false);
      setTypingLabel("typing...");
      getConversations();
    });

    channel.listenForWhisper("typing", (payload) => {
      if (!activeChat || String(payload?.from) === String(currentUser.id)) return;

      const isRelevantDirect =
        activeChat.type === "direct" && String(activeChat.receiver_id) === String(payload?.from);

      const isRelevantGroup = activeChat.type === "group";

      if (!isRelevantDirect && !isRelevantGroup) return;

      setTypingLabel(`${payload?.name || "Someone"} is typing...`);
      setIsPeerTyping(true);

      if (peerTypingTimeoutRef.current) clearTimeout(peerTypingTimeoutRef.current);
      peerTypingTimeoutRef.current = setTimeout(() => {
        setIsPeerTyping(false);
      }, 2000);
    });

    return () => {
      channel.stopListening("MessageSent");
      channel.stopListeningForWhisper("typing");
    };
  }, [activeChat, currentUser, mergeMessages, getConversations]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (peerTypingTimeoutRef.current) clearTimeout(peerTypingTimeoutRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  const toggleReactionPicker = (messageId) => {
    setOpenReactionFor((prev) => (String(prev) === String(messageId) ? null : messageId));
  };

  const addReaction = (event, messageId, emoji) => {
    event.stopPropagation();
    setMessageReactions((prev) => ({ ...prev, [messageId]: emoji }));
    setOpenReactionFor(null);
  };

  const handleBubbleTouchStart = (messageId) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setOpenReactionFor(messageId);
    }, 420);
  };

  const handleBubbleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const statusIcon = (msg) => {
    if (!msg.sent) return null;

    if (msg.failed) {
      return (
        <button
          type="button"
          title="Retry"
          className="retry-btn"
          onClick={() => retryFailedMessage(msg)}
        >
          <FiRefreshCw />
        </button>
      );
    }

    if (msg.status === "sending") {
      return (
        <span className="status-icon sending" title="Sending">
          <FiClock />
        </span>
      );
    }

    if (msg.status === "sent") {
      return (
        <span className="status-icon sent" title="Sent">
          <FiCheck />
        </span>
      );
    }

    if (msg.status === "delivered") {
      return (
        <span className="status-icon delivered" title="Delivered">
          <FiCheck />
          <FiCheck />
        </span>
      );
    }

    return (
      <span className="status-icon read" title="Read">
        <FiCheck />
        <FiCheck />
      </span>
    );
  };

  return (
    <div className="chat-app">
      <Header />
      <PageHeader title="Chat" />

      <section className="premium-chat-section">
        <div className="container">
          <div className={`user-carousel-wrapper ${activeChat ? "hide-on-mobile-active-chat" : ""}`}>
            <div className="carousel-title-row">
              <h4>
                <FaFire /> Hot now
              </h4>
              <span>
                <FaBolt /> Tap to start a flirty chat
              </span>
            </div>

            <div className="user-carousel-container">
              {showLeftArrow && (
                <button
                  className="carousel-arrow left-arrow"
                  onClick={() => scrollCarousel("left")}
                  type="button"
                >
                  <FiChevronLeft />
                </button>
              )}

              <div className="user-carousel" ref={userCarouselRef}>
                {users.map((user) => (
                  <button
                    key={`user-${user.id}`}
                    type="button"
                    className={`user-carousel-item ${
                      activeChat?.type === "direct" &&
                      String(activeChat?.receiver_id) === String(user.id)
                        ? "active"
                        : ""
                    }`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-avatar">
                      {user.imgUrl ? <img className="rounded" src={user.imgUrl} alt={user.name} /> : getInitials(user.name)}
                    </div>
                    <div className="user-name">{user.name?.split(" ")?.[0] || "Member"}</div>
                  </button>
                ))}

                {groups.map((group) => (
                  <button
                    key={`group-${group.id}`}
                    type="button"
                    className={`user-carousel-item group-item ${
                      activeChat?.type === "group" &&
                      String(activeChat?.receiver_id) === String(group.id)
                        ? "active"
                        : ""
                    }`}
                    onClick={() => handleSelectGroup(group)}
                  >
                    <div className="user-avatar group-avatar">
                      {group.imgUrl ? <img className="rounded" src={group.imgUrl} alt={group.name} /> : "G"}
                    </div>
                    <div className="user-name">{group.name?.split(" ")?.[0] || "Group"}</div>
                  </button>
                ))}
              </div>

              {showRightArrow && (
                <button
                  className="carousel-arrow right-arrow"
                  onClick={() => scrollCarousel("right")}
                  type="button"
                >
                  <FiChevronRight />
                </button>
              )}
            </div>
          </div>

          <div className={`premium-chat-container ${activeChat ? "chat-active" : ""}`}>
            <aside className="premium-chat-sidebar">
              <div className="sidebar-header">
                <h3>Messages</h3>
                <button className="notification-btn" type="button">
                  <IoMdNotificationsOutline />
                  <span className="notification-badge">
                    {conversations.reduce((acc, chat) => acc + (chat.unread || 0), 0)}
                  </span>
                </button>
              </div>

              <div className="encryption-note">
                <span className="dot" />
                <FiLock /> Private and encrypted feeling
              </div>

              <div className="chat-search">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search chats"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="text-black"
                />
              </div>

              {filteredGroups.length > 0 ? (
                <div className="group-quick-list">
                  {filteredGroups.slice(0, 8).map((group) => (
                    <button
                      key={`quick-group-${group.id}`}
                      type="button"
                      className={`group-quick-chip ${
                        activeChat?.type === "group" && String(activeChat?.receiver_id) === String(group.id)
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleSelectGroup(group)}
                    >
                      <FaFire /> {group.name}
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="chats-list">
                {loadingChats ? <p className="panel-state">Loading chats...</p> : null}

                {!loadingChats && filteredConversations.length === 0 ? (
                  <p className="panel-state">No chat found</p>
                ) : null}

                {filteredConversations.map((chat) => (
                  <button
                    key={chat.id}
                    type="button"
                    className={`chat-item ${
                      String(activeChat?.conversation_id) === String(chat.id) ? "active" : ""
                    }`}
                    onClick={() => selectConversation(chat)}
                  >
                    <div className="avatar">
                      {chat.imgUrl ? <img src={chat.imgUrl} alt={chat.name} /> : getInitials(chat.name)}
                    </div>
                    <div className="chat-info">
                      <div className="chat-name-time">
                        <h4>{chat.name}</h4>
                        <span>{chat.updatedAt ? formatTime(chat.updatedAt) : ""}</span>
                      </div>
                      <div className="chat-preview">
                        <p>{replaceEmoticons(chat.lastMsg || "Start chatting now")}</p>
                        {chat.unread > 0 ? <span className="unread-badge">{chat.unread}</span> : null}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </aside>

            <main className="premium-chat-area">
              {!activeChat ? (
                <div className="empty-chat-state">
                  <h4>Pick a chat to start</h4>
                  <p>Hot conversations, instant delivery, private feel.</p>
                </div>
              ) : (
                <>
                  <div className="chat-area-header">
                    <div className="chat-user-info">
                      <button className="back-to-list-btn" type="button" onClick={() => setActiveChat(null)} title="Back">
                        <FiChevronLeft />
                      </button>
                      <div className="avatar main-avatar">
                        {activeChat?.imgUrl ? (
                          <img src={activeChat.imgUrl} alt={activeChat.name} />
                        ) : (
                          getInitials(activeChat?.name)
                        )}
                      </div>
                      <div className="user-details">
                        <h3>{activeChat?.name}</h3>
                        <p className={`presence ${isPeerTyping ? "typing" : ""}`}>
                          {isPeerTyping ? (
                            <span className="typing-indicator-inline">
                              <span className="typing-dot" />
                              <span className="typing-dot" />
                              <span className="typing-dot" />
                              <span>{typingLabel}</span>
                            </span>
                          ) : (
                            activePresenceLabel
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="chat-actions">
                      <button className="action-btn" type="button" title="Search">
                        <FiSearch />
                      </button>
                      <button className="action-btn" type="button" title="Media">
                        <FiImage />
                      </button>
                      <button className="action-btn hot-action" type="button" title="Hot">
                        <FaHeart />
                      </button>
                    </div>
                  </div>

                  <div
                    className="messages-container"
                    ref={messagesContainerRef}
                    onScroll={handleMessagesScroll}
                  >
                    {loadingMoreMessages ? <div className="top-loader">Loading older messages...</div> : null}

                    <div className="messages-content">
                      {groupedMessages.map((cluster) => (
                        <div
                          key={cluster.id}
                          className={`message-cluster ${cluster.sent ? "sent" : "received"}`}
                        >
                          {activeChat.type === "group" && !cluster.sent ? (
                            <strong className="group-sender">{cluster.senderName}</strong>
                          ) : null}

                          {cluster.messages.map((msg, index) => {
                            const isLast = index === cluster.messages.length - 1;

                            return (
                              <div
                                key={msg.id}
                                className={`message-bubble ${msg.failed ? "failed" : ""}`}
                                onClick={() => toggleReactionPicker(msg.id)}
                                onTouchStart={() => handleBubbleTouchStart(msg.id)}
                                onTouchEnd={handleBubbleTouchEnd}
                                onTouchCancel={handleBubbleTouchEnd}
                              >
                                <p className="message-text">{msg.text}</p>

                                {msg.preview ? (
                                  <a
                                    href={msg.preview.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="message-preview-card"
                                  >
                                    <div className="preview-image">
                                      <FiLink />
                                    </div>
                                    <div className="preview-copy">
                                      <h6>{msg.preview.title}</h6>
                                      <span>{msg.preview.description}</span>
                                    </div>
                                  </a>
                                ) : null}

                                {msg.hasAttachment ? (
                                  <div className="attachment-pill">
                                    <FiFile /> Attachment shared
                                  </div>
                                ) : null}

                                <div className="message-meta">
                                  <span className="message-time">{formatTime(msg.time)}</span>
                                  {isLast ? statusIcon(msg) : null}
                                </div>

                                {messageReactions[msg.id] ? (
                                  <div className="reaction-picked">{messageReactions[msg.id]}</div>
                                ) : null}

                                {String(openReactionFor) === String(msg.id) ? (
                                  <div className="reaction-row" onClick={(event) => event.stopPropagation()}>
                                    {REACTION_SET.map((emoji) => (
                                      <button
                                        key={`${msg.id}-${emoji}`}
                                        type="button"
                                        className="reaction-btn"
                                        onClick={(event) => addReaction(event, msg.id, emoji)}
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ))}

                      <div ref={messagesEndRef} />
                    </div>

                    {lastReadMessage ? (
                      <div className="seen-now-pill">
                        <span className="seen-pulse" /> Seen just now
                      </div>
                    ) : null}
                  </div>

                  <div className="quick-replies">
                    {quickReplies.map((reply) => (
                      <button
                        key={reply}
                        type="button"
                        className="quick-reply-chip"
                        onClick={() => handleSendMessage(null, reply)}
                      >
                        <FaHeart />
                        {reply}
                      </button>
                    ))}
                  </div>

                  <form className="message-input-area" onSubmit={handleSendMessage}>
                    <div className="input-actions">
                      <button type="button" className="action-btn" title="Photo">
                        <FiImage />
                      </button>
                      <button type="button" className="action-btn hide-mobile" title="File">
                        <FiFile />
                      </button>
                      <button type="button" className="action-btn" title="Emoji">
                        <FiSmile />
                      </button>
                      <button type="button" className="action-btn hot-action" title="Hot emoji" onClick={insertHotEmoji}>
                        <FaFire />
                      </button>
                      <button type="button" className="action-btn hide-mobile" title="Voice">
                        <FiMic />
                      </button>
                    </div>

                    <input
                      type="text"
                      placeholder={`Message ${activeChat?.name || "..."}`}
                      value={message}
                      onChange={(e) => handleMessageInput(e.target.value)}
                      className="message-input text-black"
                    />

                    <button type="submit" className="send-btn" title="Send">
                      <FiSend />
                    </button>
                  </form>
                </>
              )}
            </main>
          </div>

          {groups.length === 0 ? (
            <div className="no-groups-message">
              <p>
                Want group chat energy? <Link to="/community">Join a group</Link>
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <FooterSection />

            <style jsx>{`
        * {
          box-sizing: border-box;
        }

        .chat-app {
          min-height: 100vh;
          background: #f5f9ff;
        }

        .premium-chat-section {
          padding: 1rem 0 1.6rem;
          min-height: calc(100vh - 150px);
          background: transparent;
        }

        .container {
          max-width: 1350px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .carousel-title-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.45rem;
          color: #210053;
        }

        .carousel-title-row h4 {
          margin: 0;
          font-size: 0.98rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.42rem;
        }

        .carousel-title-row span {
          font-size: 0.78rem;
          color: #df314d;
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }

        .user-carousel-wrapper {
          margin-bottom: 1rem;
        }

        .user-carousel-container {
          position: relative;
          background: #fff;
          border: 1px solid #ecf0f3;
          border-radius: 18px;
          padding: 0.65rem 2rem;
          box-shadow: 0 14px 28px rgba(33, 0, 83, 0.05);
        }

        .user-carousel {
          display: flex;
          overflow-x: auto;
          gap: 0.8rem;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding: 0.1rem 0;
        }

        .user-carousel::-webkit-scrollbar {
          display: none;
        }

        .user-carousel-item {
          background: transparent;
          border: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 72px;
          cursor: pointer;
          transition: transform 0.2s ease;
          color: #391965;
        }

        .user-carousel-item:hover {
          transform: translateY(-2px);
        }

        .user-carousel-item.active .user-avatar {
          box-shadow: 0 0 0 2px #df314d, 0 8px 14px rgba(223, 49, 77, 0.28);
        }

        .user-carousel-item.group-item .user-avatar {
          background: linear-gradient(135deg, #391965, #210053);
          color: #fff;
        }

        .user-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          overflow: hidden;
          background: #f5f9ff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 0.3rem;
          color: #df314d;
        }

        .user-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-name {
          font-size: 0.72rem;
          max-width: 70px;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .carousel-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 30px;
          height: 30px;
          border: none;
          border-radius: 50%;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #df314d;
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.13);
          z-index: 2;
        }

        .left-arrow {
          left: 0.5rem;
        }

        .right-arrow {
          right: 0.5rem;
        }

        .premium-chat-container {
          display: flex;
          background: #fff;
          border-radius: 20px;
          border: 1px solid #ecf0f3;
          overflow: hidden;
          height: 68vh;
          min-height: 500px;
          box-shadow: 0 20px 42px rgba(33, 0, 83, 0.08);
        }

        .premium-chat-sidebar {
          width: 340px;
          border-right: 1px solid #ecf0f3;
          background: #f9f9f9;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .sidebar-header {
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ecf0f3;
        }

        .sidebar-header h3 {
          margin: 0;
          color: #210053;
          font-size: 1.15rem;
          font-weight: 700;
        }

        .notification-btn {
          border: none;
          background: transparent;
          color: #391965;
          position: relative;
          font-size: 1.24rem;
        }

        .notification-badge {
          position: absolute;
          top: -4px;
          right: -6px;
          min-width: 16px;
          height: 16px;
          border-radius: 999px;
          font-size: 0.65rem;
          background: #df314d;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
        }

        .encryption-note {
          margin: 0.6rem 1rem;
          font-size: 0.75rem;
          color: #555555;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-weight: 500;
        }

        .encryption-note .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #0be881;
          box-shadow: 0 0 0 4px rgba(11, 232, 129, 0.12);
        }

        .chat-search {
          padding: 0 1rem 0.9rem;
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 1.8rem;
          top: 40%;
          transform: translateY(-50%);
          color: #a0a6bc;
        }

        .chat-search input {
          width: 100%;
          border: 1px solid #ecf0f3;
          border-radius: 999px;
          padding: 0.65rem 0.95rem 0.65rem 2.2rem;
          font-size: 0.9rem;
          background: #fff;
          outline: none;
          color: #210053;
          font-weight: 500;
        }

        .chat-search input::placeholder {
          color: #a0a6bc;
          opacity: 1;
        }

        .chat-search input:focus {
          border-color: #391965;
          box-shadow: 0 0 0 3px rgba(57, 25, 101, 0.12);
        }

        .chats-list {
          overflow-y: auto;
          flex: 1;
        }

        .panel-state {
          color: #555555;
          font-size: 0.84rem;
          text-align: center;
          padding: 1rem;
        }

        .chat-item {
          width: 100%;
          text-align: left;
          border: none;
          background: transparent;
          border-bottom: 1px solid #ecf0f3;
          padding: 0.8rem 0.95rem;
          display: flex;
          gap: 0.7rem;
          align-items: flex-start;
          transition: background 0.2s ease;
          cursor: pointer;
        }

        .chat-item:hover {
          background: #f5f9ff;
        }

        .chat-item.active {
          background: rgba(223, 49, 77, 0.05);
          box-shadow: inset 3px 0 0 #df314d;
        }

        .group-quick-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          padding: 0 1rem 0.75rem;
        }

        .group-quick-chip {
          border: 1px solid #df314d;
          border-radius: 999px;
          background: #fff;
          color: #df314d !important;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.28rem;
          padding: 0.24rem 0.58rem;
          cursor: pointer;
        }

        .group-quick-chip.active,
        .group-quick-chip:hover {
          background: #df314d;
          color: #fff !important;
        }

        .avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #f5f9ff;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          color: #df314d;
          overflow: hidden;
          text-transform: uppercase;
        }

        .avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .chat-info {
          flex: 1;
          min-width: 0;
        }

        .chat-name-time {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.4rem;
          margin-bottom: 0.22rem;
        }

        .chat-name-time h4 {
          margin: 0;
          font-size: 0.93rem;
          color: #210053;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .chat-name-time span {
          font-size: 0.68rem;
          color: #a0a6bc;
        }

        .chat-preview {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.6rem;
        }

        .chat-preview p {
          margin: 0;
          color: #555555;
          font-size: 0.84rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 500;
        }

        .unread-badge {
          min-width: 19px;
          height: 19px;
          border-radius: 999px;
          background: #df314d;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.67rem;
          font-weight: 700;
          padding: 0 5px;
        }

        .premium-chat-area {
          display: flex;
          flex-direction: column;
          flex: 1;
          background: #fff;
          position: relative;
          min-height: 0;
        }

        .empty-chat-state {
          margin: auto;
          text-align: center;
          color: #a0a6bc;
        }

        .empty-chat-state h4 {
          margin-bottom: 0.3rem;
          font-size: 1.2rem;
          color: #391965;
        }

        .chat-area-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #ecf0f3;
          background: #fff;
          z-index: 2;
        }

        .chat-user-info {
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }
        
        .back-to-list-btn {
          display: none;
          background: transparent;
          border: none;
          color: #df314d;
          font-size: 1.6rem;
          cursor: pointer;
          padding: 0;
          margin-right: 0.5rem;
          align-items: center;
          justify-content: center;
        }

        .main-avatar {
          width: 42px;
          height: 42px;
        }

        .user-details h3 {
          margin: 0;
          font-size: 1rem;
          color: #210053;
        }

        .presence {
          margin: 0;
          font-size: 0.76rem;
          color: #a0a6bc;
          min-height: 16px;
          font-weight: 500;
        }

        .presence.typing {
          color: #df314d;
          font-weight: 600;
        }

        .typing-indicator-inline {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
        }

        .typing-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #df314d;
          animation: typingBounce 1s infinite ease-in-out;
        }

        .typing-dot:nth-child(2) {
          animation-delay: 0.15s;
        }

        .typing-dot:nth-child(3) {
          animation-delay: 0.3s;
        }

        .chat-actions {
          display: flex;
          gap: 0.22rem;
        }

        .action-btn {
          border: none;
          background: transparent;
          color: #391965;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease;
          font-size: 1rem;
        }

        .action-btn:hover {
          background: #f5f9ff;
          color: #df314d;
        }

        .hot-action {
          color: #df314d;
        }

        .messages-container {
          overflow-y: auto;
          flex: 1;
          padding: 0.95rem 1rem;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          background: #fcfcfc;
          min-height: 0;
        }

        .top-loader {
          text-align: center;
          font-size: 0.76rem;
          color: #a0a6bc;
          margin-bottom: 0.4rem;
        }

        .messages-content {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }

        .message-cluster {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          max-width: min(72%, 550px);
          animation: popIn 0.2s ease;
        }

        .message-cluster.sent {
          margin-left: auto;
          align-items: flex-end;
        }

        .message-cluster.received {
          margin-right: auto;
          align-items: flex-start;
        }

        .group-sender {
          font-size: 0.72rem;
          color: #391965;
          margin-left: 0.4rem;
        }

        .message-bubble {
          width: fit-content;
          max-width: 100%;
          padding: 0.5rem 0.72rem;
          border-radius: 14px;
          position: relative;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
          cursor: pointer;
        }

        .message-cluster.received .message-bubble {
          background: #fff;
          border-top-left-radius: 4px;
          border: 1px solid #ecf0f3;
        }

        .message-cluster.sent .message-bubble {
          background: #df314d;
          border-top-right-radius: 4px;
          border: 1px solid #c92c45;
          color: #fff;
        }

        .message-bubble.failed {
          border: 1px solid #ea7b74;
          background: #fff4f2;
        }

        .message-text {
          margin: 0;
          font-size: 0.93rem;
          line-height: 1.4;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .message-cluster.received .message-text {
          color: #210053;
        }
        .message-cluster.sent .message-text {
          color: #fff;
        }

        .message-preview-card {
          margin-top: 0.4rem;
          border-radius: 10px;
          overflow: hidden;
          text-decoration: none;
          border: 1px solid rgba(0, 0, 0, 0.07);
          display: grid;
          grid-template-columns: 56px 1fr;
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(8px);
        }

        .preview-image {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #391965;
          background: #f5f9ff;
          font-size: 1rem;
        }

        .preview-copy {
          padding: 0.32rem 0.55rem;
        }

        .preview-copy h6 {
          margin: 0;
          color: #210053;
          font-size: 0.77rem;
          font-weight: 700;
        }

        .preview-copy span {
          display: block;
          margin-top: 0.15rem;
          color: #555555;
          font-size: 0.7rem;
        }

        .attachment-pill {
          margin-top: 0.35rem;
          display: inline-flex;
          gap: 0.22rem;
          align-items: center;
          background: rgba(223, 49, 77, 0.12);
          color: #df314d;
          padding: 0.16rem 0.5rem;
          border-radius: 999px;
          font-size: 0.68rem;
        }
        .message-cluster.sent .attachment-pill {
           background: rgba(255, 255, 255, 0.2);
           color: #fff;
        }

        .message-meta {
          margin-top: 0.2rem;
          display: flex;
          gap: 0.3rem;
          align-items: center;
          justify-content: flex-end;
        }

        .reaction-row {
          margin-top: 0.35rem;
          display: inline-flex;
          gap: 0.24rem;
          padding: 0.15rem 0.2rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 2px 7px rgba(0, 0, 0, 0.11);
        }

        .reaction-btn {
          border: none;
          background: transparent;
          font-size: 0.95rem;
          line-height: 1;
          cursor: pointer;
          padding: 0.1rem 0.2rem;
        }

        .reaction-picked {
          margin-top: 0.25rem;
          font-size: 0.88rem;
        }

        .seen-now-pill {
          margin: 0.55rem auto 0;
          width: fit-content;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.18rem 0.58rem;
          border-radius: 999px;
          background: rgba(223, 49, 77, 0.14);
          color: #df314d;
          font-size: 0.72rem;
          font-weight: 700;
        }

        .seen-pulse {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #df314d;
          animation: pulseDot 1.35s infinite;
        }

        .message-time {
          font-size: 0.69rem;
          color: #a0a6bc;
          font-weight: 600;
        }
        .message-cluster.sent .message-time {
          color: #fcebeb;
        }

        .status-icon {
          display: inline-flex;
          align-items: center;
          gap: 0;
          font-size: 0.7rem;
        }

        .status-icon.sending {
          color: #fcebeb;
        }

        .status-icon.sent {
          color: #fff;
        }

        .status-icon.delivered {
          color: #fff;
        }

        .status-icon.read {
          color: #0be881;
        }

        .retry-btn {
          border: none;
          background: transparent;
          color: #d9433b;
          font-size: 0.75rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
        }

        .quick-replies {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          padding: 0.55rem 0.8rem 0.25rem;
          border-top: 1px solid #ecf0f3;
          background: #fff;
          min-height: 42px;
        }

        .quick-reply-chip {
          border: 1px solid #df314d;
          border-radius: 999px;
          background: #fff;
          color: #df314d !important;
          font-size: 0.78rem;
          padding: 0.34rem 0.7rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-weight: 600;
          opacity: 1 !important;
          line-height: 1.2 !important;
        }

        .quick-reply-chip:hover {
          background: #df314d;
          color: #fff !important;
        }

        .message-input-area {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          border-top: 1px solid #ecf0f3;
          padding: 0.55rem 0.75rem;
          background: #fff;
          min-height: 58px;
        }

        .message-input-area .action-btn {
          color: #391965 !important;
          background: transparent;
        }
        .message-input-area .action-btn:hover {
          color: #df314d !important;
        }

        .input-actions {
          display: flex;
          gap: 0.1rem;
        }

        .message-input {
          flex: 1;
          border: none;
          background: #f5f9ff;
          border-radius: 999px;
          padding: 0.62rem 0.9rem;
          font-size: 0.9rem;
          outline: none;
          color: #210053;
          font-weight: 500;
          box-shadow: inset 0 0 0 1px #ecf0f3;
        }

        .message-input::placeholder {
           color: #a0a6bc;
        }

        .message-input:focus {
          box-shadow: inset 0 0 0 2px #df314d;
        }

        .send-btn {
          width: 42px;
          height: 42px;
          flex-shrink: 0;
          border-radius: 50%;
          border: none;
          background: #df314d;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.2rem;
          box-shadow: 0 4px 10px rgba(223, 49, 77, 0.3);
          transition: transform 0.2s;
        }

        .send-btn:hover {
          background: #c92c45;
          transform: scale(1.05);
        }

        .no-groups-message {
          margin-top: 0.85rem;
          background: #fff;
          border: 1px solid #ecf0f3;
          border-radius: 14px;
          padding: 0.7rem 0.9rem;
          text-align: center;
          color: #391965;
          font-size: 0.83rem;
        }

        .no-groups-message p {
          margin: 0;
        }

        .no-groups-message a {
          color: #df314d;
          font-weight: 700;
          text-decoration: none;
        }

        .no-groups-message a:hover {
          text-decoration: underline;
        }

        @keyframes popIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes typingBounce {
          0%,
          80%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          40% {
            transform: translateY(-3px);
            opacity: 1;
          }
        }

        @keyframes pulseDot {
          0% {
            box-shadow: 0 0 0 0 rgba(223, 49, 77, 0.5);
          }
          70% {
            box-shadow: 0 0 0 8px rgba(223, 49, 77, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(223, 49, 77, 0);
          }
        }

        @keyframes sendGlow {
          0% {
            box-shadow: 0 0 0 0 rgba(223, 49, 77, 0.45);
          }
          70% {
            box-shadow: 0 0 0 12px rgba(223, 49, 77, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(223, 49, 77, 0);
          }
        }

        @media (max-width: 1024px) {
          .premium-chat-sidebar {
            width: 300px;
          }
          .message-cluster {
            max-width: 78%;
          }
        }

        @media (max-width: 820px) {
          .hide-on-mobile-active-chat {
             display: none !important;
          }
          .premium-chat-container {
             display: flex;
             flex-direction: column;
             height: calc(100vh - 160px);
             min-height: auto;
          }
          .premium-chat-container.chat-active {
             height: calc(100vh - 140px);
          }
          .premium-chat-sidebar {
             width: 100%;
          }
          .premium-chat-container.chat-active .premium-chat-sidebar {
             display: none;
          }
          .premium-chat-area {
             width: 100%;
          }
          .premium-chat-container:not(.chat-active) .premium-chat-area {
             display: none;
          }
          
          .back-to-list-btn {
            display: flex;
          }

          .container {
            padding: 0;
          }

          .carousel-arrow {
            display: none;
          }

          .message-cluster {
            max-width: 85%;
          }
        }

        @media (max-width: 560px) {
          .premium-chat-section {
            padding: 0;
            min-height: auto;
          }

          .user-carousel-container {
            padding: 0.52rem 0.6rem;
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .user-avatar {
            width: 46px;
            height: 46px;
          }

          .premium-chat-container {
            border-radius: 0;
            border-left: none;
            border-right: none;
            border-bottom: none;
            height: calc(100vh - 160px);
          }
          .premium-chat-container.chat-active {
            height: calc(100vh - 130px);
          }
          .hide-mobile {
            display: none !important;
          }

          .chat-area-header,
          .message-input-area {
            padding-left: 0.55rem;
            padding-right: 0.55rem;
          }

          .messages-container {
            padding: 0.7rem 0.55rem;
          }

          .quick-replies {
            padding: 0.48rem 0.55rem 0.2rem;
            gap: 0.35rem;
          }

          .quick-reply-chip {
            font-size: 0.71rem;
            padding: 0.3rem 0.56rem;
          }

          .message-cluster {
            max-width: 92%;
          }

          .message-bubble {
            padding: 0.46rem 0.62rem;
          }

          .message-text {
            font-size: 0.85rem;
          }

          .chat-actions .action-btn,
          .input-actions .action-btn {
            width: 30px;
            height: 30px;
          }

          .send-btn {
            width: 40px;
            height: 40px;
            font-size: 1.1rem;
          }
          
          .message-input {
            padding: 0.5rem 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatPage;
